/**
 * RANK PANEL (JARVIS-80) — pops down from the rank badge (same feel as the adult
 * weather hero → forecast pop-out). One row per ladder tier: earned ranks show the
 * insignia, earned date, clearance features and a REPLAY chip for their classified
 * file (requeues the DeclassifyReveal ceremony — grants NOTHING); the next rank
 * shows live progress; future tiers are dimmed with a redacted, non-descriptive
 * file teaser when a dossier file is waiting there.
 *
 * CLEARANCE FEATURES double as the reward roadmap: live capabilities are bright ✅,
 * locked ones are dim but READABLE on purpose — the advertisement is the motivation
 * (video calling at Officier, remote DJ authority at Chief). Dossier FILES are
 * the opposite: zero content until earned.
 *
 * Self-contained styles (DadLink pattern) — kid.scss is owned elsewhere; nothing
 * here touches it. z-index 41 (scrim 40): under RankUp/DeclassifyReveal (50) and
 * the DadLink overlay (60), above the kid-center.
 */
import RankInsignia from "./RankInsignia";
import type { ChildState } from "./useChildState";

type DossierFile = { title: string; rank: string; fact: string };

// Reward roadmap, keyed by ladder rank. `live` = the capability actually exists on
// the kid surface today; a feature stays 🔜 even after the rank is earned until its
// patch ships (JARVIS-79 video must land its own ≥Lead gate — no theater).
// Every future kid-surface patch should land as a line here.
const CLEARANCE_FEATURES: Record<string, { label: string; live: boolean }[]> = {
  "Cadet": [
    { label: "CONSOLE ACCESS", live: true },
    { label: "JARVIS VOICE LINK", live: true },
    { label: "LEARNING BAYS", live: true },
  ],
  "Recrue": [
    { label: "FIELD MISSIONS & EXPERIMENTS", live: true },
    { label: "PROJECT LOG", live: true },
  ],
  "Spécialiste": [
    { label: "HOUSE MUSIC COMMAND — THE WHOLE-HOUSE AUDIO ANSWERS TO YOU", live: true },
  ],
  // Operator re-slot 2026-07-11: DAD LINK voice = the Lead unlock (tile + call_dad
  // + persona all gate on rank ≥ Lead); VIDEO = the Staff unlock (JARVIS-79 must
  // gate kid-side video ≥ Lieutenant so the promise is real).
  "Officier": [
    { label: "DAD LINK — VOICE CALLING WITH DAD", live: true },
    { label: "DEEPER ARCHIVE CLEARANCE", live: false },
  ],
  "Lieutenant": [
    { label: "DAD LINK — VIDEO CALLING WITH DAD", live: false },
  ],
  "Commandant": [
    { label: "COMMAND CLEARANCE — HOUSE SYSTEMS ANSWER TO THIS RANK (COMMISSIONING)", live: false },
  ],
  "Chef Ingénieur": [
    { label: "REMOTE DJ AUTHORITY — PLAY ONE SONG PER HOUR ON DAD'S HOME SYSTEM", live: false },
    { label: "FURTHER SYSTEMS — PENDING COMMISSION", live: false },
  ],
};

function earnedDate(rank: string, promotions: { rank: string; at: number }[]): string {
  if (rank === "Cadet") return "DAY ONE";
  // Last promotion entry wins (demo swaps can re-promote; the latest is his real climb).
  const hit = [...promotions].reverse().find((p) => p.rank === rank);
  if (!hit || !hit.at) return "ON RECORD";
  const d = new Date(hit.at);
  return d
    .toLocaleDateString([], { year: "numeric", month: "2-digit", day: "2-digit" })
    .replace(/\//g, ".");
}

export default function RankPanel({
  state,
  onReplay,
  onClose,
}: {
  state: ChildState;
  onReplay: (file: DossierFile) => void;
  onClose: () => void;
}) {
  const ladder = state.ladder || [];
  const curIdx = ladder.indexOf(state.rank);
  const promotions = state.promotions || [];
  const dossierTiers = state.dossierTiers || [];
  const cleared = state.dossier?.cleared || [];

  return (
    <>
      <style>{CSS}</style>
      <div className="kid-rankpanel__scrim" onClick={onClose} />
      <div className="kid-rankpanel" role="dialog" aria-label="Rank record">
        <span className="kid-rankpanel__corner kid-rankpanel__corner--tl" />
        <span className="kid-rankpanel__corner kid-rankpanel__corner--tr" />
        <span className="kid-rankpanel__corner kid-rankpanel__corner--bl" />
        <span className="kid-rankpanel__corner kid-rankpanel__corner--br" />
        <div className="kid-rankpanel__head">
          <span>◈ DOSSIER DE SERVICE · ISAAC ◈</span>
          <button className="kid-rankpanel__close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {ladder.map((rank, i) => {
          const isEarned = i <= curIdx;
          const isCurrent = i === curIdx;
          const isNext = i === curIdx + 1;
          const file = cleared.find((f) => f.rank === rank) || null;
          const hasLockedFile = !isEarned && dossierTiers.includes(rank);
          const feats = CLEARANCE_FEATURES[rank] || [];
          const cls = [
            "kid-rankpanel__row",
            isEarned ? "is-earned" : "is-future",
            isCurrent ? "is-current" : "",
            isNext ? "is-next" : "",
          ].join(" ");
          return (
            // The WHOLE earned row is the replay target (operator 2026-07-11): kid
            // taps are broad — anywhere on an unlocked tier requeues its file
            // (story narration + the King-Lives Elvis score included). The chip
            // stays as the visual affordance.
            <div
              key={rank}
              className={cls}
              style={file ? { cursor: "pointer" } : undefined}
              onClick={file ? () => onReplay(file) : undefined}
            >
              <div className="kid-rankpanel__insignia">
                <RankInsignia rank={rank} size={26} />
              </div>
              <div className="kid-rankpanel__body">
                <div className="kid-rankpanel__title">
                  <span className="kid-rankpanel__rank">{rank.toUpperCase()}</span>
                  {isCurrent && <span className="kid-rankpanel__tag">CURRENT RANK</span>}
                  {isEarned && !isCurrent && (
                    <span className="kid-rankpanel__date">EARNED {earnedDate(rank, promotions)}</span>
                  )}
                  {isCurrent && (
                    <span className="kid-rankpanel__date">EARNED {earnedDate(rank, promotions)}</span>
                  )}
                  {isNext && <span className="kid-rankpanel__tag kid-rankpanel__tag--next">IN PROGRESS</span>}
                </div>

                {isNext && state.nextThreshold ? (
                  <div className="kid-rankpanel__progress">
                    <div className="kid-rankpanel__progresslabel">
                      {state.correctCount}/{state.nextThreshold}
                    </div>
                    <div className="kid-rankpanel__bar">
                      <div
                        className="kid-rankpanel__fill"
                        style={{
                          width: `${Math.min(100, (state.correctCount / (state.nextThreshold || 1)) * 100)}%`,
                        }}
                      />
                    </div>
                  </div>
                ) : null}

                <div className="kid-rankpanel__feats">
                  {feats.map((f) => {
                    const unlocked = isEarned && f.live;
                    const commissioning = isEarned && !f.live;
                    return (
                      <div
                        key={f.label}
                        className={
                          "kid-rankpanel__feat" + (unlocked ? " is-live" : " is-locked")
                        }
                      >
                        <span className="kid-rankpanel__featmark">
                          {unlocked ? "✅" : commissioning ? "🔜" : "🔒"}
                        </span>
                        <span>
                          {f.label}
                          {!isEarned && " — LOCKED"}
                          {commissioning && " — COMMISSIONING"}
                        </span>
                      </div>
                    );
                  })}
                </div>

                {file && (
                  <button
                    className="kid-rankpanel__filechip"
                    onClick={(e) => { e.stopPropagation(); onReplay(file); }}
                  >
                    ▣ CLASSIFIED FILE — REPLAY ▸
                  </button>
                )}
                {hasLockedFile && (
                  <div className="kid-rankpanel__filechip kid-rankpanel__filechip--redacted">
                    <span className="kid-rankpanel__lock">🔒</span> ▮▮▮▮▮▮▮▮▮▮ · CLEARANCE REQUIRED
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

const CSS = `
.kid-rankpanel__scrim {
  position: absolute; inset: 0; z-index: 40;
  background: rgba(6, 10, 16, 0.82);
}
.kid-rankpanel {
  position: absolute; z-index: 41;
  top: clamp(58px, 5.4vw, 96px);
  left: clamp(20px, 2.1vw, 40px);
  width: min(560px, 62vw);
  max-height: calc(100vh - clamp(80px, 8vw, 140px));
  overflow-y: auto;
  padding: 14px 18px 18px;
  background: rgba(8, 14, 22, 0.96);
  border: 1px solid rgba(126, 224, 255, 0.35);
  color: #7ee0ff;
  font-family: var(--kid-font-mono);
  letter-spacing: 0.08em;
  animation: kid-rankpanel-drop 0.28s cubic-bezier(0.2, 0.9, 0.3, 1) both;
}
@keyframes kid-rankpanel-drop {
  from { opacity: 0; transform: translateY(-14px); }
  to   { opacity: 1; transform: translateY(0); }
}
.kid-rankpanel__corner {
  position: absolute; width: 12px; height: 12px;
  border: 1.5px solid #7ee0ff; opacity: 0.9;
}
.kid-rankpanel__corner--tl { top: -1px; left: -1px; border-right: none; border-bottom: none; }
.kid-rankpanel__corner--tr { top: -1px; right: -1px; border-left: none; border-bottom: none; }
.kid-rankpanel__corner--bl { bottom: -1px; left: -1px; border-right: none; border-top: none; }
.kid-rankpanel__corner--br { bottom: -1px; right: -1px; border-left: none; border-top: none; }
.kid-rankpanel__head {
  display: flex; align-items: center; justify-content: space-between;
  font-size: clamp(12px, 1vw, 17px);
  opacity: 0.85; margin-bottom: 10px; text-transform: uppercase;
}
.kid-rankpanel__close {
  background: none; border: 1px solid rgba(126, 224, 255, 0.4);
  color: #7ee0ff; font: inherit; padding: 2px 9px; cursor: pointer;
}
.kid-rankpanel__row {
  display: flex; gap: 12px; align-items: flex-start;
  padding: 10px 8px; border: 1px solid transparent;
  border-top: 1px solid rgba(126, 224, 255, 0.12);
}
.kid-rankpanel__row.is-current {
  border: 1px solid rgba(126, 224, 255, 0.55);
  background: rgba(126, 224, 255, 0.06);
}
.kid-rankpanel__row.is-future { opacity: 0.45; }
.kid-rankpanel__row.is-next { opacity: 0.8; }
.kid-rankpanel__insignia { flex: 0 0 auto; padding-top: 2px; }
.kid-rankpanel__body { flex: 1 1 auto; min-width: 0; }
.kid-rankpanel__title {
  display: flex; align-items: baseline; gap: 10px; flex-wrap: wrap;
  font-size: clamp(13px, 1.1vw, 19px); text-transform: uppercase;
}
.kid-rankpanel__rank { font-weight: 700; }
.kid-rankpanel__tag {
  font-size: 0.7em; border: 1px solid rgba(126, 224, 255, 0.6);
  padding: 1px 6px; letter-spacing: 0.14em;
}
.kid-rankpanel__tag--next { border-color: rgba(126, 224, 255, 0.35); }
.kid-rankpanel__date { font-size: 0.72em; opacity: 0.65; letter-spacing: 0.14em; }
.kid-rankpanel__progress { display: flex; align-items: center; gap: 10px; margin-top: 6px; }
.kid-rankpanel__progresslabel { font-size: clamp(11px, 0.85vw, 15px); opacity: 0.85; }
.kid-rankpanel__bar { flex: 1 1 auto; height: 4px; background: rgba(126, 224, 255, 0.12); }
.kid-rankpanel__fill { height: 100%; background: #7ee0ff; box-shadow: 0 0 6px rgba(126, 224, 255, 0.7); }
.kid-rankpanel__feats { margin-top: 6px; display: flex; flex-direction: column; gap: 3px; }
.kid-rankpanel__feat {
  display: flex; gap: 7px; align-items: baseline;
  font-size: clamp(11px, 0.85vw, 15px);
}
.kid-rankpanel__feat.is-live { opacity: 0.95; }
.kid-rankpanel__feat.is-locked { opacity: 0.7; }
.kid-rankpanel__featmark { flex: 0 0 auto; font-size: 0.9em; }
.kid-rankpanel__filechip {
  display: inline-flex; align-items: center; gap: 7px; margin-top: 8px;
  background: rgba(126, 224, 255, 0.08); border: 1px solid rgba(126, 224, 255, 0.5);
  color: #7ee0ff; font: inherit; font-size: clamp(11px, 0.9vw, 16px);
  letter-spacing: 0.1em; padding: 5px 11px; cursor: pointer; text-transform: uppercase;
}
.kid-rankpanel__filechip:active { background: rgba(126, 224, 255, 0.2); }
.kid-rankpanel__filechip--redacted {
  cursor: default; color: rgba(126, 224, 255, 0.55);
  border-color: rgba(126, 224, 255, 0.25); background: rgba(126, 224, 255, 0.03);
}
.kid-rankpanel__lock { font-size: 0.9em; }
`;
